class UTMTracker {
  // ─── Singleton guard ────────────────────────────────────────────
  static _instance = null;

  constructor() {
    if (UTMTracker._instance) {
      return UTMTracker._instance;
    }
    UTMTracker._instance = this;

    this.utmParams = this.extractUTMParams();
    this.init();
  }

  extractUTMParams() {
    const urlParams = new URLSearchParams(window.location.search);
    const utmParams = {};
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].forEach(key => {
      if (urlParams.has(key)) utmParams[key] = urlParams.get(key);
    });
    // Echte Quelle bleibt erhalten; den Preiskanal waehlt die Preis-Function
    return utmParams;
  }

storeUTMParams() {
    if (Object.keys(this.utmParams).length > 0) {
      sessionStorage.setItem('utm_params', JSON.stringify(this.utmParams));
      sessionStorage.setItem('utm_timestamp', Date.now().toString());
    }
  }

  async setCustomerTypeAttribute() {
    // Shopify exposes customer state via meta - check if logged in
    const customerType = window.__st?.cid 
      ? (document.body.dataset.customerTags?.includes('Premium') ? 'premium' : 'kunde')
      : 'guest';

    try {
      await fetch('/cart/update.json', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: { customer_type: customerType }
        })
      });
    } catch (error) {
      console.error('Error setting customer_type cart attribute:', error);
    }
  }

  getStoredUTMParams() {
    const stored = sessionStorage.getItem('utm_params');
    return stored ? JSON.parse(stored) : {};
  }

  hasValidUTMSession() {
    const storedParams = this.getStoredUTMParams();
    const timestamp = sessionStorage.getItem('utm_timestamp');
    return Object.keys(storedParams).length > 0 && !!timestamp;
  }

  async clearCartAttributes() {
    try {
      const response = await fetch('/cart/update.json', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: { utm_source: '', utm_source_original: '', utm_medium: '', utm_campaign: '' }
        })
      });
      if (!response.ok) console.error('Failed to clear cart attributes');
    } catch (error) {
      console.error('Error clearing cart attributes:', error);
    }
  }

  async addUTMToCart() {
    let utmParams = {};
    let hasCurrentUTM = false;

    if (this.utmParams.utm_source) {
      utmParams = this.utmParams;
      hasCurrentUTM = true;
    } else if (this.hasValidUTMSession()) {
      utmParams = this.getStoredUTMParams();
      hasCurrentUTM = true;
    }

    try {
      if (hasCurrentUTM && utmParams.utm_source) {
        // Echte Quelle; welchen Preis sie bekommt, entscheidet die Preis-Function
        // (hdc-digital-app, cart-transformer-2) ueber das Shop-Metafeld custom.price_channel_map.
        const response = await fetch('/cart/update.json', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            attributes: {
              utm_source: utmParams.utm_source,
              // Altlast der Zwischenversion entfernen (leer = Attribut wird geloescht)
              utm_source_original: '',
              utm_medium: utmParams.utm_medium || '',
              utm_campaign: utmParams.utm_campaign || ''
            }
          })
        });
        if (!response.ok) console.error('Failed to update cart with UTM parameters');
      } else {
        await this.clearCartAttributes();
      }
    } catch (error) {
      console.error('Error updating cart with UTM parameters:', error);
    }
  }

  hookCartOperations() {
    // ── Form submits ──────────────────────────────────────────────
    document.addEventListener('submit', async (event) => {
      if (event.target.matches('form[action*="/cart/add"]')) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        await this.addUTMToCart();
      }
    });

    // ── Fetch hook — ONLY intercept /cart/add, never /cart/update ─
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      const url = typeof args[0] === 'string' ? args[0] : (args[0]?.url || '');
      const response = await originalFetch(...args);

      if (url.includes('/cart/add') && !url.includes('/cart/update')) {
        setTimeout(() => this.addUTMToCart(), 1000);
      }

      return response;
    };

    // ── jQuery hook ───────────────────────────────────────────────
    if (window.jQuery) {
      const originalAjax = window.jQuery.ajax;
      window.jQuery.ajax = function(options) {
        const result = originalAjax.apply(this, arguments);
        if (options.url && options.url.includes('/cart/add')) {
          result.done(() => UTMTracker._instance?.addUTMToCart());
        }
        return result;
      };
    }
  }

init() {
    this.storeUTMParams();
    this.hookCartOperations();
    this.setCustomerTypeAttribute();

    const run = () => setTimeout(() => this.handleInitialCart(), 1500);
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', run);
    } else {
      run();
    }
  }

  async handleInitialCart() {
    await this.addUTMToCart();
  }

  // ─── Static helpers — NO new UTMTracker() ────────────────────────
  static addToCart() {
    UTMTracker._instance?.addUTMToCart();
  }

  static getCurrentSource() {
    // Read directly from URL / sessionStorage — never instantiate
    const urlParams = new URLSearchParams(window.location.search);
    const urlSource = urlParams.get('utm_source');
    if (urlSource) return urlSource;

    const stored = sessionStorage.getItem('utm_params');
    if (stored) {
      try {
        const params = JSON.parse(stored);
        if (params.utm_source) return params.utm_source;
      } catch (_) {}
    }

    return 'direct';
  }

  static clearUTMTracking() {
    sessionStorage.removeItem('utm_params');
    sessionStorage.removeItem('utm_timestamp');
    UTMTracker._instance?.clearCartAttributes();
    console.log('UTM tracking cleared');
  }
}

// ─── Bootstrap (once) ─────────────────────────────────────────────────────────
if (typeof window !== 'undefined') {
  window.UTMTracker = UTMTracker;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => new UTMTracker());
  } else {
    new UTMTracker();
  }
}

window.addUTMToCart        = UTMTracker.addToCart;
window.getCurrentUTMSource = UTMTracker.getCurrentSource;
window.clearUTMTracking    = UTMTracker.clearUTMTracking;